import {useMemo, useState, type ReactNode} from "react";
import cn from "classnames";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";

import {ALL_PRODUCT, CONTENT_TYPES_WITHOUT_LAYOUTS, CHANNELS_WITHOUT_LAYOUTS} from "data/constants";
import {findChannelObj, isContentTypeSelectable} from "data/products";
import {useSaveLayoutOrder} from "hooks/useLayoutOrder";
import {codeLayoutOptions, layoutIconChannelOf} from "libs/layoutOffer";
import {
  ALL_PRODUCTS_SCOPE,
  defaultLayoutId,
  hiddenLayoutsKey,
  layoutOrderKey,
  layoutOrderOf,
  orderedLayoutOptions,
  stageHasProduct,
  type ChannelSettingsLike,
  type HiddenChange,
} from "libs/selectorVisibility";
import type {LayoutOption} from "libs/layoutOptions";
import {layoutIconFor} from "../LayoutSelector";
import CheckboxGroup from "./CheckboxGroup";

/**
 * The layouts that the Layout selector offers, per stage (rows) and layout
 * (columns), for the page's set. An admin clears a checkbox and the layout
 * leaves the selector of that stage and set: for the page's product, or for
 * every product. Only the layouts the templates draw are listed
 * (libs/layoutOffer.ts). A link with `?layout=` still opens a hidden layout.
 *
 * A layout hidden for every product stays hidden for each product: in the
 * product scope its box is cleared and disabled. The last offered layout of a
 * row cannot be cleared: a page must offer one.
 *
 * An admin drags a column to put the layouts in another order, for every
 * stage of the set, for the product or for every product (`layoutOrders`).
 * The Layout selector shows its buttons in that order, and the first offered
 * layout of a row is the default layout of that stage (outlined). A product
 * with no order of its own uses the order for every product. Each drop saves
 * at once.
 */

interface Option {
  label: string;
  value: string;
}

/** One checkbox of SelectorVisibilitySection: its input and its shown state. */
export type OptionCheckbox = (
  option: Omit<HiddenChange, "hidden">,
  isHiddenOnServer: boolean,
  props?: {label?: string; disabled?: boolean; title?: string}
) => {isHidden: boolean; input: ReactNode};

interface Row {
  type: Option & {defaultLayout?: number};
  options: LayoutOption[];
}

/** A column head: the layout icon, dragged to reorder the columns. */
function SortableHead({
  id,
  isDisabled,
  children,
}: {
  id: number;
  isDisabled: boolean;
  children: ReactNode;
}) {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id,
    disabled: isDisabled,
  });
  return (
    <span
      ref={setNodeRef}
      className={cn("layout-visibility-head", {"is-sortable": !isDisabled})}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : "auto",
        position: "relative",
        opacity: isDragging ? 0.6 : 1,
        // Without this a touch-drag scrolls the sidebar instead of moving the column.
        touchAction: "none",
      }}
      {...attributes}
      {...listeners}
      aria-label={`Layout ${id}`}
      title={isDisabled ? `Layout ${id}` : `Layout ${id}: drag to change the order`}
    >
      {children}
    </span>
  );
}

/**
 * The rows of the table: each stage of the scope whose templates draw more
 * than one layout for the set. With one layout there is nothing to choose.
 * No product is the scope of every product. The stages of a product include
 * the ones an admin put it on (`settings`, the data channel's).
 */
export function layoutVisibilityRows({
  dataChannel,
  set,
  product,
  settings,
}: {
  dataChannel: string;
  set: string | undefined;
  product: string | null;
  settings: ChannelSettingsLike;
}): Row[] {
  if (CHANNELS_WITHOUT_LAYOUTS.includes(dataChannel)) return [];
  const types: Row["type"][] = findChannelObj(dataChannel)?.types ?? [];
  return types
    .filter(
      (type) =>
        isContentTypeSelectable(type.value) &&
        !CONTENT_TYPES_WITHOUT_LAYOUTS.includes(type.value) &&
        (product === null ||
          // The releases "All Products" view is listed on every stage.
          product === ALL_PRODUCT.value ||
          stageHasProduct(settings, dataChannel, type.value, product))
    )
    .map((type) => ({
      type,
      options: codeLayoutOptions({dataChannel, type: type.value, set, product}),
    }))
    .filter((row) => row.options.length > 1);
}

export default function LayoutVisibilityGroup({
  dataChannel,
  entryChannel,
  product,
  set: setOption,
  currentType,
  settings,
  checkbox,
}: {
  dataChannel: string;
  /** The entry channel: the inline icons of the Layout selector follow it. */
  entryChannel: string;
  product: Option;
  /** The page's releases set, or undefined off releases. */
  set: Option | undefined;
  currentType: string;
  settings: ChannelSettingsLike;
  checkbox: OptionCheckbox;
}) {
  const set = setOption?.value;
  const [isAllProducts, setIsAllProducts] = useState(false);
  const scopeProduct = isAllProducts ? ALL_PRODUCTS_SCOPE : product.value;
  const iconChannel = layoutIconChannelOf({dataChannel, set, product: product.value});
  const {save: saveOrder, hasFailed: hasOrderFailed} = useSaveLayoutOrder(dataChannel);
  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  // A server from before the layout orders sends no `layoutOrders`: it would
  // refuse the write.
  const knowsOrders = settings?.layoutOrders !== undefined;
  // The order of the scope: in the product scope, the product's own order or
  // else the order for every product.
  const order = layoutOrderOf(settings, scopeProduct, set);
  const hasOwnOrder = (settings?.layoutOrders?.[layoutOrderKey(scopeProduct, set)] ?? []).length > 0;

  const rows: Row[] = useMemo(
    () =>
      layoutVisibilityRows({
        dataChannel,
        set,
        product: isAllProducts ? null : product.value,
        settings,
      }),
    [dataChannel, set, product.value, isAllProducts, settings]
  );

  const columns: LayoutOption[] = useMemo(() => {
    const seen = new Map<number, LayoutOption>();
    for (const row of rows) {
      for (const option of row.options) if (!seen.has(option.id)) seen.set(option.id, option);
    }
    return orderedLayoutOptions([...seen.values()], order);
  }, [rows, order]);

  if (rows.length === 0) return null;

  const isSortable = knowsOrders && columns.length > 1;
  const orderScope = {product: scopeProduct, ...(set ? {set} : {})};
  const onDragEnd = ({active, over}: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = columns.map((column) => column.id);
    const from = ids.indexOf(Number(active.id));
    const to = ids.indexOf(Number(over.id));
    if (from < 0 || to < 0) return;
    saveOrder({...orderScope, order: arrayMove(ids, from, to)});
  };

  const lists = settings?.hiddenLayouts ?? {};
  const hiddenIn = (type: string, scope: string) =>
    new Set(lists[hiddenLayoutsKey(type, scope, set)] ?? []);

  const table = rows.map((row) => {
    const forAll = hiddenIn(row.type.value, ALL_PRODUCTS_SCOPE);
    const own = hiddenIn(row.type.value, scopeProduct);
    const cells = columns.map((column) => {
      if (!row.options.some((option) => option.id === column.id)) return null;
      const isHiddenForAll = !isAllProducts && forAll.has(column.id);
      return {column, isHiddenForAll, isHiddenOnServer: isHiddenForAll || own.has(column.id)};
    });
    return {row, cells};
  });

  // The summary counts the hidden layouts of the page's own row.
  const currentRow = table.find(({row}) => row.type.value === currentType) ?? table[0];
  let hiddenHere = 0;
  const rendered = table.map(({row, cells}) => {
    const items = cells.map((cell) => {
      if (!cell) return null;
      const option = {
        channel: dataChannel,
        kind: "layout" as const,
        type: row.type.value,
        product: scopeProduct,
        ...(set ? {set} : {}),
        value: cell.column.id,
      };
      return {cell, option};
    });
    // A first pass for the shown state, so the last offered box can be locked.
    const states = items.map((item) =>
      item ? checkbox(item.option, item.cell.isHiddenOnServer).isHidden : null
    );
    const offered = states.filter((isHidden) => isHidden === false).length;
    if (row === currentRow.row) hiddenHere = states.filter((isHidden) => isHidden).length;
    const hidden = new Set(
      columns.filter((_column, index) => states[index]).map((column) => column.id)
    );
    const defaultId = defaultLayoutId(
      row.type.defaultLayout ?? 1,
      orderedLayoutOptions(row.options, order),
      hidden,
      order
    );
    return {
      row,
      defaultId,
      boxes: items.map((item, index) => {
        if (!item) return null;
        const isLast = states[index] === false && offered === 1;
        const disabled = item.cell.isHiddenForAll || isLast;
        return checkbox(item.option, item.cell.isHiddenOnServer, {
          label: `Layout ${item.cell.column.id} on ${row.type.label}`,
          disabled,
          title: item.cell.isHiddenForAll
            ? "Hidden for every product"
            : isLast
              ? "A page must offer one layout"
              : undefined,
        });
      }),
    };
  });

  return (
    <CheckboxGroup
      title={`Layouts${setOption ? ` of ${setOption.label}` : ""}`}
      summary={
        hiddenHere > 0
          ? `${hiddenHere} of ${currentRow.cells.filter(Boolean).length} hidden here`
          : "all shown here"
      }
      isChanged={hiddenHere > 0}
      className="layout-visibility"
    >
      <div className="layout-visibility-scope" role="radiogroup" aria-label="Layout rule scope">
        <label>
          <input type="radio" checked={!isAllProducts} onChange={() => setIsAllProducts(false)} />
          {product.label}
        </label>
        <label>
          <input type="radio" checked={isAllProducts} onChange={() => setIsAllProducts(true)} />
          All products
        </label>
      </div>
      {isSortable && (
        <div className="layout-visibility-order">
          <span>Drag a layout to reorder. The first shown one is the default.</span>
          {hasOwnOrder && (
            <button
              type="button"
              className="layout-visibility-order-reset"
              onClick={() => saveOrder({...orderScope, order: []})}
            >
              {isAllProducts ? "Use the code order" : "Use the order of all products"}
            </button>
          )}
        </div>
      )}
      <div
        className="layout-visibility-matrix"
        style={{gridTemplateColumns: `minmax(56px, auto) repeat(${columns.length}, 1fr)`}}
      >
        <span />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={columns.map((column) => column.id)}
            strategy={horizontalListSortingStrategy}
          >
            {columns.map((column) => {
              const icon = layoutIconFor({
                channelValue: entryChannel,
                typeValue: currentType,
                iconChannel,
                option: column,
              });
              return (
                <SortableHead key={column.id} id={column.id} isDisabled={!isSortable}>
                  <span
                    className="layout-visibility-icon"
                    style={icon.src ? {backgroundImage: `url(${icon.src})`} : undefined}
                  >
                    {icon.inline}
                  </span>
                </SortableHead>
              );
            })}
          </SortableContext>
        </DndContext>
        {rendered.map(({row, defaultId, boxes}) => (
          <div key={row.type.value} className="layout-visibility-row">
            <span
              className={cn("layout-visibility-stage", {
                "is-current": row.type.value === currentType,
              })}
            >
              {row.type.label}
            </span>
            {boxes.map((box, index) => {
              const isDefault = Boolean(box) && columns[index].id === defaultId;
              return (
                <span
                  key={columns[index].id}
                  className={cn("layout-visibility-cell", {
                    "is-hidden": box?.isHidden,
                    "is-default": isDefault,
                  })}
                  title={isDefault ? `Default layout of ${row.type.label}` : undefined}
                >
                  {box?.input}
                </span>
              );
            })}
          </div>
        ))}
      </div>
      {hasOrderFailed && (
        <span className="default-scene-status warning">
          Couldn&apos;t save the layout order. The table shows what the server has.
        </span>
      )}
    </CheckboxGroup>
  );
}
